import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, MessageCircle, X } from "lucide-react";
import { NAV_LINKS, WA_DEFAULT, WHATSAPP_DISPLAY } from "../data/site";
import { EASE, scrollToHash } from "../lib/motion";
import LogoMark from "./LogoMark";

const go = (e, href) => {
  e.preventDefault();
  scrollToHash(href);
  window.dispatchEvent(new CustomEvent("close-mobile-nav"));
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    const close = () => setOpen(false);
    window.addEventListener("close-mobile-nav", close);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("close-mobile-nav", close);
    };
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled || open
          ? "border-b border-[#E7E3D8] bg-[#FAF9F6]/90 backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8">
        <a
          href="#top"
          onClick={(e) => go(e, "#top")}
          data-testid="nav-logo"
          className="flex items-center gap-3"
          aria-label="Shivaay Consultant — home"
        >
          <span className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl ring-1 ring-gold/30">
            <img
              src="/shivaay-logo.png"
              alt="Shivaay Consultant logo"
              className="h-full w-full object-cover"
            />
          </span>
          <span className="leading-none">
            <span className="font-serif text-[1.35rem] font-semibold tracking-wide text-navy">
              Shivaay
            </span>
            <span className="mt-0.5 block text-[9px] font-semibold uppercase tracking-[0.4em] text-gold-deep">
              Consultant
            </span>
          </span>
        </a>

        <nav className="hidden items-center gap-9 md:flex">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              onClick={(e) => go(e, l.href)}
              className="text-sm font-semibold text-navy/70 transition-colors hover:text-gold-deep"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={WA_DEFAULT}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-whatsapp-cta"
            className="hidden items-center gap-2 rounded-full bg-wa px-5 py-2.5 text-sm font-bold text-white shadow-[0_6px_20px_rgba(31,175,94,0.35)] transition-all hover:bg-[#189a51] sm:flex"
          >
            <MessageCircle className="h-4 w-4" />
            {WHATSAPP_DISPLAY}
          </a>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="rounded-full border border-navy/15 p-2.5 text-navy md:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="overflow-hidden border-b border-[#E7E3D8] bg-[#FAF9F6]/95 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`mobile-nav-link-${l.href.slice(1)}`}
                  onClick={(e) => go(e, l.href)}
                  className="rounded-lg px-3 py-3 text-base font-medium text-navy transition-colors hover:bg-sand"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={WA_DEFAULT}
                target="_blank"
                rel="noreferrer"
                data-testid="mobile-nav-whatsapp-cta"
                className="mt-2 flex items-center justify-center gap-2 rounded-full bg-wa px-5 py-3 text-sm font-bold text-white"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp {WHATSAPP_DISPLAY}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
